import { useCallback, useMemo, useState } from "react";
import Fuse, { type Expression, type FuseOptionKey } from "fuse.js";

const DEFAULT_FUSE_THRESHOLD = 0.15;

export interface FuseConfig<T extends object> {
  keys: Array<FuseOptionKey<T>>;
  threshold?: number;
}

const useTableDataFilter = <T extends object, F extends Partial<T>>(
  data: Array<T>,
  filter: F,
  fuseConfig: FuseConfig<T>,
  idKey: keyof T,
) => {
  const [filterData, setFilterData] = useState<Array<T>>([]);
  const [filterQuery, setFilterQuery] = useState<F>(filter);
  const [hasRanFuseQuery, setHasRanFuseQuery] = useState(false);

  const fuse = useMemo(() => {
    return new Fuse(data, {
      keys: fuseConfig.keys,
      threshold: fuseConfig.threshold || DEFAULT_FUSE_THRESHOLD,
    });
  }, [fuseConfig, data]);

  const handleFilterUpdate = useCallback((prop: keyof T, value: T[keyof T]) => {
    setFilterQuery((prevState) => ({
      ...prevState,
      [prop]: value,
    }));
  }, []);

  const applyFilter = useCallback(() => {
    const fuseQuery: Expression[] = [];

    Object.keys(filterQuery).forEach((k) => {
      const filterValue = filterQuery[k as keyof T];

      if (Array.isArray(filterValue)) {
        if (!filterValue.length) {
          return;
        }

        const orGroup: Array<Expression> = filterValue.map((v) => ({
          [k]: v,
        }));
        fuseQuery.push({ $or: orGroup });
        return;
      }

      if (filterValue) {
        fuseQuery.push({ [k]: filterValue });
      }
    });

    if (!fuseQuery.length) {
      setFilterData([]);
      setHasRanFuseQuery(false);
      return;
    }

    const fuseSearchResult = fuse.search({
      $and: fuseQuery,
    });

    setFilterData(fuseSearchResult.map(({ item }) => item));
    setHasRanFuseQuery(true);
  }, [filterQuery, fuse]);

  const getValueFromFilterQuery = useCallback(
    (prop: keyof F) => {
      return filterQuery[prop];
    },
    [filterQuery],
  );

  const filteredData = useMemo(() => {
    if (!hasRanFuseQuery) {
      return data;
    }

    const ids = filterData.map((fd) => fd[idKey]);
    return data.filter((d) => ids.includes(d[idKey]));
  }, [data, filterData, idKey, hasRanFuseQuery]);

  return {
    filteredData,
    handleFilterUpdate,
    applyFilter,
    getValueFromFilterQuery,
  };
};

export default useTableDataFilter;
